import * as THREE from 'three'
import { ProceduralCity } from './ProceduralCity'
import { asianCities,type AsianWorldId } from './asianWorlds'

const cream='#e5d9bf'
/** Shared construction parts; roofs have curved slopes and lifted eaves, not cone stand-ins. */
export class AsianArchitecture extends ProceduralCity {
  protected curvedHip:THREE.BufferGeometry
  protected roundRoof:THREE.BufferGeometry
  protected cap=new THREE.SphereGeometry(1,64,32,0,Math.PI*2,0,Math.PI/2)
  constructor(readonly city:AsianWorldId){
    super()
    const points:number[]=[],indices:number[]=[],steps=7
    for(let j=0;j<=steps;j++){
      const t=j/steps,r=.12+.88*t,y=Math.pow(1-t,2)*.85+Math.pow(t,10)*.16
      for(const [x,z] of [[-1,-1],[1,-1],[1,1],[-1,1]])points.push(x*r*.5,y,z*r*.5)
    }
    for(let j=0;j<steps;j++)for(let k=0;k<4;k++){const a=j*4+k,b=j*4+(k+1)%4,c=(j+1)*4+k,d=(j+1)*4+(k+1)%4;indices.push(a,b,c,b,d,c)}
    this.curvedHip=new THREE.BufferGeometry();this.curvedHip.setAttribute('position',new THREE.Float32BufferAttribute(points,3));this.curvedHip.setIndex(indices);this.curvedHip.computeVertexNormals()
    this.roundRoof=new THREE.LatheGeometry([[1,.14],[.8,.13],[.55,.31],[.3,.6],[.13,.86],[0,1]].map(([x,y])=>new THREE.Vector2(x,y)),32)
    for(const g of [this.curvedHip,this.roundRoof,this.cap])this.geometries.add(g)
  }
  protected eastRoof(x:number,y:number,z:number,w:number,d:number,h:number,color:string=asianCities[this.city].roof){
    const m=this.mat(color)
    this.add(this.curvedHip,m,x,y,z,w,h,d)
    this.box(x,y+h*.85,z,w*.14,.1,d*.14,color)
    for(const side of [-1,1]){
      this.box(x,y+h*.14,z+side*d/2,w,.09,.1,color);this.box(x+side*w/2,y+h*.14,z,.1,.09,d,color)
      // Tile ribs follow the concave pitch down each long slope.
      for(let dx=-w*.4;dx<=w*.41;dx+=w/10)for(let j=0;j<7;j++){
        const a=.12+j*.88/7,b=.12+(j+1)*.88/7,yy=(t:number)=>y+h*(Math.pow((1-t)/.88,2)*.85+Math.pow((t-.12)/.88,10)*.16)
        this.beam(new THREE.Vector3(x+dx*a,yy(a)+.055,z+side*d*a/2),new THREE.Vector3(x+dx*b,yy(b)+.055,z+side*d*b/2),.018,color)
      }
    }
  }
  protected eastWindow(x:number,y:number,z:number,w:number,h:number,lattice=false){
    const p=asianCities[this.city];this.box(x,y,z,w+.13,h+.13,.08,lattice?p.wood:cream);this.box(x,y,z+.05,w,h,.05,lattice?'#d2c4a4':'#557e87')
    for(let dx=-w/2+.16;dx<w/2;dx+=lattice?.22:w/2)this.box(x+dx,y,z+.09,.027,h,.035,lattice?p.wood:'#bbc8c1')
    this.box(x,y,z+.09,w,.035,.035,lattice?p.wood:'#bbc8c1');this.box(x,y-h/2-.09,z+.1,w+.2,.1,.18,cream)
  }
  protected hall(x:number,z:number,w:number,d:number,h:number,royal=false){
    const p=asianCities[this.city],wood=royal?(this.city==='tokyo'?'#b65843':'#995344'):p.wood
    this.box(x,.55,z,w+.6,.4,d+.6,cream);this.box(x,h/2+.7,z,w*.87,h,d*.85,royal?'#ccac85':p.walls[0])
    for(let face=0;face<4;face++)this.at(x,z,1,face*Math.PI/2,()=>{
      const ww=face%2?d:w,zz=(face%2?w:d)/2
      const bays=Math.max(1,Math.floor(ww/1.3)),step=ww/bays
      for(let i=0;i<=bays;i++)this.disc(-ww/2+i*step,h/2+.7,zz,.075,h,wood)
      for(let i=0;i<bays;i++)this.eastWindow(-ww/2+(i+.5)*step,h*.53+.6,zz-.12,Math.min(.77,step*.62),h*.57,true)
      for(const yy of [h+.45,h+.67])this.box(0,yy,zz,ww,.14,.24,p.accent)
      if(royal)for(let a=-ww/2+.3;a<ww/2;a+=.65){this.box(a,h+.38,zz+.15,.27,.32,.38,p.accent);this.box(a,h+.47,zz+.23,.4,.08,.15,'#d1ad61')}
    })
    this.eastRoof(x,h+.8,z,w+1,d+1,Math.max(.85,w*.12),royal&&this.city==='beijing'?'#c69e4d':p.roof)
  }
  protected urbanBlock(x:number,z:number,w:number,d:number,h:number,seed:number,glass=false){
    const p=asianCities[this.city];this.box(x,h/2+.32,z,w,h,d,glass?this.facade(seed%2?'teal':'blue'):p.walls[seed%5])
    for(let face=0;face<4;face++)this.at(x,z,1,face*Math.PI/2,()=>{
      const ww=face%2?d:w,zz=(face%2?w:d)/2+.04
      for(let y=1.1;y<h;y+=1.25){this.box(0,y+.59,zz,ww,.09,.07,'#a6b2ae');for(let xx=-ww/2+.48;xx<ww/2-.1;xx+=.95)this.eastWindow(xx,y,zz,.61,.83)}
      for(const xx of [-ww/2+.08,ww/2-.08])this.box(xx,h/2+.3,zz,.12,h,.14,cream)
    })
    this.box(x,h+.38,z,w+.13,.14,d+.13,'#aeb6ab');this.box(x-.6,h+.63,z-.4,.65,.4,.7,'#7c8d8b')
    for(const side of [-1,1])this.box(x+side*(w/2-.04),h+.62,z,.12,.45,d,'#b6bfb4')
  }
  protected tree(x:number,z:number,s=1,blossom=false){
    this.disc(x,1.15*s,z,.09*s,2.3*s,'#786855');for(const [dx,dy,dz] of [[0,2.6,0],[-.5,2.15,.1],[.5,2.22,.2]])this.ball(x+dx*s,dy*s,z+dz*s,.75*s,blossom?'#d9a6b0':'#789d78',.78)
  }
  protected lamp(x:number,z:number){this.disc(x,1.6,z,.04,3.2,'#435b5b');this.box(x,3.05,z,.34,.35,.34,'#edcf93');this.box(x,3.29,z,.5,.09,.5,'#4a6661')}
  protected lantern(x:number,y:number,z:number,s=.3){this.ball(x,y,z,s,this.city==='seoul'?'#d7ad70':'#c36c53',1.25);for(const yy of [-1,1])this.disc(x,y+yy*s,z,s*.52,.09,'#91715a');this.disc(x,y-s*1.65,z,.02,s*.8,'#c6a36c')}
  protected terrace(){this.box(0,.15,0,24,.2,24,'#bcb6a3');this.box(0,.29,0,23.5,.08,23.5,'#d6cdb7')}
  protected steps(x:number,z:number,w:number,n=4){for(let i=0;i<n;i++)this.box(x,.33+i*.13,z-i*.35,w-i*.25,.2,.6,cream)}
}
